import React from 'react'
import { Outlet } from 'react-router-dom'
import styled from 'styled-components'
import { Icon } from '@devnw/icons'
import { asUnified, ut } from '@devnw/theme'
// @devnw/layout's package.json only declares a "./*" wildcard export map (no
// per-subpath entries), so eslint-import-resolver-node (wired in
// .eslintrc.json) cannot follow it even though it resolves correctly at
// build/runtime (webpack, ts-jest) — see src/components/PamBreadcrumbs.tsx:28
// for the identical, pre-existing pattern this mirrors.
// eslint-disable-next-line import/no-unresolved
import { AppShell } from '@devnw/layout/AppShell/AppShell'
// eslint-disable-next-line import/no-unresolved
import Sidebar from '@devnw/layout/layout/Sidebar'
import { useAuth } from '../auth/AuthContext'
import { useIsNarrow } from '../hooks/useIsNarrow'
import { TenantSwitcher, useActiveTenant } from './TenantSwitcher'
import { TenantGate } from './TenantGate'
import { AdminNav } from './AdminNav'

/** isNavItemVisible now lives in AdminNav.tsx (Task 3 extraction), which
 *  both the desktop sidebar and mobile drawer render — re-exported here so
 *  __tests__/AdminLayout.test.tsx's existing `from '../src/layout/AdminLayout'`
 *  import keeps working unchanged. */
export { isNavItemVisible } from './AdminNav'

// MINOR-7 (final-review fix wave): this feeds AppShell's `sidebarWidth` —
// the DESKTOP PERMANENT sidebar — not the mobile drawer. The actual
// off-canvas drawer's width is fixed inside @devnw/layout's Drawer/Panel at
// `min(360px, 85%)` and is not configurable from here at all, so the old
// name `DRAWER_WIDTH` named the wrong component.
const SIDEBAR_WIDTH = 240

// ---------------------------------------------------------------------------
// Themed shell.
//
// Renders through @devnw/layout's AppShell (Task 3, OIDCPAM Gate-6 Phase 8)
// instead of a hand-rolled fixed-position header/sidebar/main tree: the
// previous shell had a fixed 240px `position: fixed` sidebar always mounted
// and `margin-left: 240px` content, with no viewport logic anywhere, so a
// narrow screen had no way to dismiss the sidebar. AppShell supplies the
// header/sidebar/content *containers* (surface colour, borders, padding);
// useIsNarrow() drives `showSidebar`, and an off-canvas @devnw/layout
// `Sidebar` drawer (mounted only on narrow viewports) renders the exact same
// <AdminNav/> as the permanent desktop sidebar, so the two can never diverge.
//
// HeaderInner/Brand/Spacer/WhoAmI/LogoutButton are unchanged from the
// pre-AppShell shell (only the outer fixed-position HeaderBar wrapper they
// used to sit in was deleted — AppShell's own header View replaces it). All
// theme lookups go through `ut(asUnified(theme))` / optional-chained
// `asUnified(theme)` reads with literal fallbacks, so nothing here throws if
// ever rendered without a styled-components ThemeProvider.
// ---------------------------------------------------------------------------

// Kept as a semantic <header> (was a plain styled.div before this migration)
// so the banner landmark role the deleted HeaderBar used to carry
// (`<header role="banner">`) survives even though HeaderBar itself is gone —
// a nested <header> not inside another sectioning element still exposes the
// implicit ARIA "banner" role.
const HeaderInner = styled.header`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
  width: 100%;
  padding: 0 ${({ theme }) => ut(asUnified(theme)).space('lg')};
  box-sizing: border-box;
`

const Brand = styled.h1`
  margin: 0;
  flex-shrink: 0;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.lg ?? '1.25rem'};
  font-weight: 600;
`

const Spacer = styled.div`
  flex: 1;
`

const WhoAmI = styled.span`
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  margin-right: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  opacity: 0.9;
`

// IMPORTANT-2 (final-review fix wave): border/hover/focus-ring here were
// written for the DELETED HeaderBar, whose background was `colors.primary`
// (#1a56db) — translucent white read fine against that dark blue. AppShell
// now renders the header on `theme.colors.surface` (`toSpyderTheme` maps
// this to ExtendedTheme's `bg.surface`, #f3f4f6 in light mode) — a near-white
// surface, against which rgba(255,255,255,*) is nearly invisible at any
// opacity. Measured contrast of the focus ring alone: old (#fff on #1a56db)
// 6.18:1; new, unfixed (#fff on #f3f4f6) 1.10:1 — fails WCAG 2.2 SC 2.4.11
// (focus indicators need >=3:1 against the adjacent background). Switching
// to the theme's primary colour (matching NavItemLink's own
// `asUnified(theme).colors?.primary` pattern in AdminNav.tsx) restores
// visibility: #1a56db on #f3f4f6 computes to ~5.6:1 by the same WCAG
// relative-luminance formula, comfortably clearing the 3:1 floor.
const LogoutButton = styled.button`
  min-height: 36px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('sm')}`};
  border: 1px solid
    ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}99`};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: transparent;
  color: inherit;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}14`};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 2px;
  }
`

// New (Task 3): the narrow-viewport hamburger that opens the off-canvas
// Sidebar drawer. Styled consistently with LogoutButton (icon-sized instead
// of text-sized). IMPORTANT-2 (final-review fix wave): same hardcoded-white-
// on-the-wrong-background bug as LogoutButton above (this button is new in
// this branch, and was written against the same now-deleted dark HeaderBar
// background) — same theme-token fix, same rationale.
const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 36px;
  min-width: 36px;
  padding: ${({ theme }) => ut(asUnified(theme)).space('xxs')};
  border: 1px solid
    ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}99`};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}14`};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 2px;
  }
`

// New (Task 3): AppShell's content View has no padding/overflow of its own
// (it only sets flex + background) — this replaces the deleted `Main`'s
// padding/scroll behaviour without reintroducing its `margin-left`/
// `position: fixed` rules, and keeps the `role="main"` landmark the deleted
// `<Main role="main">` used to carry (a bare <main> exposes it implicitly).
const ContentPad = styled.main`
  padding: ${({ theme }) => ut(asUnified(theme)).space('lg')};
  overflow: auto;
  box-sizing: border-box;
`

export const AdminLayout = () => {
  const {
    claims,
    logout,
    adminRole,
    permissions,
    permissionsStatus,
    refreshPermissions,
  } = useAuth()
  // Active tenant resolved by TenantProvider. Used to (a) gate child pages
  // until a tenant is known and (b) re-key the outlet so a tenant switch
  // remounts the page and re-runs its mount fetch against the new tenant.
  const { tenant, tenantError } = useActiveTenant()

  // isNavItemVisible's fail-closed contract treats `permissions === null` as
  // "unresolved" — collapse 'idle'/'loading' to null here so a caller whose
  // whoami fetch hasn't started/settled never has nav items computed from a
  // stale/default array.
  //
  // IMPORTANT-2 (fix-wave 2026-07-24): 'error' is deliberately NOT collapsed
  // to null here anymore. AuthContext.fetchPermissions intentionally leaves a
  // PREVIOUSLY resolved `permissions`/`adminRole` untouched on a transient
  // refetch failure (see its comment) precisely so an already-authorized
  // admin's nav doesn't silently blank out — but this line used to discard
  // that preserved value anyway, making the documented behavior inert. Using
  // `permissions` directly on 'error' honors it: `null` if whoami has never
  // once resolved (still correctly hidden), or the last-known-good array if
  // it has (nav keeps working; the banner below surfaces the failure).
  const resolvedPermissions =
    permissionsStatus === 'idle' || permissionsStatus === 'loading'
      ? null
      : permissions

  // Task 3: viewport-driven responsive shell. Below NAV_BREAKPOINT_PX
  // (useIsNarrow's default), the permanent AppShell sidebar is hidden in
  // favour of a hamburger + off-canvas Sidebar drawer.
  const isNarrow = useIsNarrow()
  const [drawerOpen, setDrawerOpen] = React.useState(false)

  // Collapsing back to the desktop layout must not strand an open drawer —
  // otherwise widening the viewport while the drawer is open would leave
  // `drawerOpen` stale/true (the Sidebar below already visually closes via
  // its `open={isNarrow && drawerOpen}` prop, per MINOR-4 below, but this
  // state itself needs resetting too), so a subsequent narrow transition
  // would show it already "open" with no click.
  React.useEffect(() => {
    if (!isNarrow) setDrawerOpen(false)
  }, [isNarrow])

  // Rendered by BOTH the desktop AppShell sidebar slot and the mobile
  // Sidebar drawer below — never two separate instances, so the nav can
  // never drift out of sync between the two surfaces. Only one of the two
  // ever actually shows content at a time: AppShell hides its sidebar slot
  // via `showSidebar={!isNarrow}`, and the Sidebar drawer (MINOR-4, final-
  // review fix wave: kept unconditionally MOUNTED now, unlike its earlier
  // `isNarrow && <Sidebar>` gating — see its own comment below) renders
  // nothing whenever its `open={isNarrow && drawerOpen}` prop is false.
  const nav = (
    <AdminNav
      adminRole={adminRole}
      resolvedPermissions={resolvedPermissions}
      permissionsStatus={permissionsStatus}
      onRefreshPermissions={() => {
        refreshPermissions().catch(() => {
          // Intentional no-op: refreshPermissions already routes its own
          // failure into permissionsStatus === 'error' — this catch exists
          // only so a rejected promise from the retry click handler itself
          // can never surface as an unhandled rejection.
        })
      }}
      onNavigate={isNarrow ? () => setDrawerOpen(false) : undefined}
    />
  )

  const header = (
    <HeaderInner>
      {/* MINOR-3 (final-review fix wave): three fixes together.
          (1) Real toggle — was `setDrawerOpen(true)` unconditionally, so a
              second activation (e.g. a screen reader's browse-mode virtual
              cursor reaching this button while the dialog is open, which
              isn't blocked the way a real Tab-trapped keyboard user would
              be) was a no-op instead of closing the drawer.
          (2) `aria-haspopup="dialog"` instead of `aria-expanded`. ARIA APG
              reserves `aria-expanded` for disclosure widgets whose expanded
              content lives inline in the same subtree (accordions, menu
              buttons) — the state literally IS "expanded" vs "collapsed".
              This button instead opens a separate `role="dialog"
              aria-modal="true"` overlay (@devnw/layout's Drawer), which is
              exactly the case ARIA APG's dialog-modal pattern says to mark
              with `aria-haspopup="dialog"` on the invoking button, not
              `aria-expanded` — there is no "expanded" state to contradict,
              since a screen reader user was previously hearing "expanded"
              while the drawer was open with no way to toggle it shut from
              here, which read as broken.
          (3) Accessible name reflects state ("Open" vs "Close navigation")
              so the name itself carries the state `aria-expanded` used to
              (incorrectly) convey. */}
      {isNarrow && (
        <MenuButton
          type="button"
          aria-label={drawerOpen ? 'Close navigation' : 'Open navigation'}
          aria-haspopup="dialog"
          onClick={() => setDrawerOpen((open) => !open)}
        >
          <Icon name="bars" ariaHidden size="sm" />
        </MenuButton>
      )}
      <Brand>OIDC Admin</Brand>
      <TenantSwitcher />
      <Spacer />
      <WhoAmI>{claims?.sub ?? ''}</WhoAmI>
      <LogoutButton type="button" onClick={logout} aria-label="Logout">
        Logout
      </LogoutButton>
    </HeaderInner>
  )

  return (
    <>
      <AppShell
        testID="admin-shell"
        sidebarWidth={SIDEBAR_WIDTH}
        showSidebar={!isNarrow}
        sidebar={nav}
        header={header}
      >
        <ContentPad>
          {tenant === '' ? (
            // Active tenant not yet resolved. Hold the outlet so child pages
            // don't mount + fire requests with an empty tenantId → server 404
            // "Tenant not found". TenantGate (./TenantGate.tsx, shared with
            // BareLayout) distinguishes a genuinely in-flight resolution
            // attempt (spinner) from an exhausted one (accessible alert).
            <TenantGate tenantError={tenantError} />
          ) : (
            // key={tenant} remounts the page subtree on a tenant switch so
            // each page re-runs its useResource mount fetch against the new
            // tenant.
            <div key={tenant}>
              <Outlet />
            </div>
          )}
        </ContentPad>
      </AppShell>

      {/* MINOR-4 (final-review fix wave): previously gated by `{isNarrow &&
          <Sidebar ...>}`, which unmounted this Sidebar/Drawer the instant
          `isNarrow` flips false — in the SAME render as this component's own
          `!isNarrow` effect (above) eventually sets `drawerOpen` false, but
          strictly before that effect gets to run. @devnw/layout's Drawer
          only restores focus to the pre-open element via a `useEffect` keyed
          on its `open` prop transitioning true -> false; a component that
          has already been unmounted never gets to observe that transition
          (there is no "closing" render for a component that's simply gone),
          so the restore was skipped entirely — verified with a focus() spy,
          the restore attempt was never even made.
          Folding `isNarrow` into the `open` expression itself — instead of
          into the mount condition — keeps Drawer mounted across the
          transition, so its `open` prop still genuinely flips true -> false
          in this same render, and its focus-restore effect actually fires
          (same spy: now called) before Drawer renders nothing.
          Residual, NOT fully closed by this fix: the restore target is
          MenuButton (below), which is itself unmounted by this same
          `isNarrow` flip (it is only rendered `isNarrow && ...`) — so in
          THIS app, the restore attempt still lands on a just-detached node
          and focus still falls through to <body>. Fully closing that would
          mean keeping MenuButton itself mounted through the transition,
          which is a larger change than this fix's scope; falling back to
          <body> when the entire responsive chrome reorganises underneath an
          open modal is a defensible, common default (no keyboard trap
          results — Tab from <body> reaches the newly-shown desktop sidebar
          normally), so it is left as a follow-up rather than expanded here. */}
      <Sidebar
        open={isNarrow && drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Navigation"
      >
        {nav}
      </Sidebar>
    </>
  )
}
